// Top-level Personas route container. Agent scoped = that agent's personas;
// no agent = the real org-wide list across every agent. This container owns
// the URL state: the `?persona=` drawer subject, the `?new=persona` create
// drawer. A scope switch with the drawer open strips the subject so a stale
// drawer never survives into another agent's view.
import { useCallback, useEffect, useRef } from "react";

import {
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { parsePersonasSearch, type PersonasSearch } from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { Button, Page } from "../ui";
import { PersonasList } from "./personas/PersonasList";

type PersonasSearchState = PersonasSearch & WorkingScopeSearch;

export function Personas() {
  const { agentId } = useWorkingScope();
  const navigate = useTypedNavigate<PersonasSearchState>();
  const search = useTypedSearch(withWorkingScopeSearch(parsePersonasSearch));

  // Functional-merge navigation ONLY: patch this route's own params over the
  // previous search so the root-owned `?agent` scope always survives.
  const patchSearch = useCallback(
    (patch: Partial<PersonasSearch>, replace: boolean) => {
      void navigate({
        replace,
        search: mergeSearch<PersonasSearchState>(patch),
      });
    },
    [navigate],
  );
  // One drawer at a time (§4a): opening either subject clears the other;
  // closes are replaces so Back never reopens a dismissed drawer.
  const openCreate = useCallback(() => {
    patchSearch({ new: "persona", persona: undefined }, false);
  }, [patchSearch]);
  const openEdit = useCallback(
    (personaId: string) => {
      patchSearch({ new: undefined, persona: personaId }, false);
    },
    [patchSearch],
  );
  const closeEditors = useCallback(() => {
    patchSearch({ new: undefined, persona: undefined }, true);
  }, [patchSearch]);

  // A drawer subject never survives an agent-scope switch (markers.md URL
  // state): any change of scope with a drawer open strips the params.
  const previousAgentIdRef = useRef(agentId);
  const hasOpenDrawer =
    search.persona !== undefined || search.new !== undefined;
  useEffect(() => {
    if (previousAgentIdRef.current === agentId) {
      return;
    }
    previousAgentIdRef.current = agentId;
    if (hasOpenDrawer) {
      closeEditors();
    }
  }, [agentId, closeEditors, hasOpenDrawer]);

  return (
    <Page variant="workspace">
      <PersonasList
        agentId={agentId}
        createAction={
          <Button size="sm" onClick={openCreate}>
            Create persona
          </Button>
        }
        editingPersonaId={search.persona ?? null}
        isCreateOpen={search.new === "persona"}
        onCloseEditor={closeEditors}
        onOpenEdit={openEdit}
      />
    </Page>
  );
}
